import { PROVIDERS, type AgentProvider, type MemoryKeeper } from "@codvero/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { accountsApi, modelsApi } from "@/api/accounts.ts";
import { api, get } from "@/api/client.ts";
import { MemoryEditor } from "@/features/dock/MemoryEditor.tsx";
import { Alert, Button, Card, Field, Select, Spinner } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";

interface MyMemory { path: string; content: string; exists: boolean; running: boolean }
const myMemoryApi = {
  get: () => get<MyMemory>("/api/my-memory"),
  save: (content: string) => api<MyMemory>("/api/my-memory", { method: "PUT", json: { content } }),
  keeper: () => get<MemoryKeeper>("/api/my-memory/keeper"),
  setKeeper: (accountId: string, model: string) => api<MemoryKeeper>("/api/my-memory/keeper", { method: "PUT", json: { accountId, model } }),
};

/** Who keeps the conversations' memories after each turn: one provider account and its model (default: Claude, Haiku). */
function KeeperPicker() {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const k = useQuery({ queryKey: ["memory-keeper"], queryFn: myMemoryApi.keeper });
  const accounts = (useQuery({ queryKey: ["accounts"], queryFn: accountsApi.list, staleTime: 60_000 }).data ?? []).filter((a) => a.status === "connected");
  const [accountId, setAccountId] = useState("");
  const [model, setModel] = useState("");
  useEffect(() => { if (k.data && !accountId) { setAccountId(k.data.accountId ?? ""); setModel(k.data.model); } }, [k.data]);
  const acc = accounts.find((a) => a.id === accountId);
  const provider = (acc?.provider ?? "claude") as AgentProvider;
  const catalog = useQuery({ queryKey: ["models", provider, acc?.id ?? null], queryFn: () => modelsApi.catalog(provider, acc?.id ?? null), enabled: !!acc, staleTime: 5 * 60_000 });
  const models = catalog.data?.models ?? PROVIDERS[provider].models.map((m) => ({ id: m.id, label: m.label }));
  const list = models.some((m) => m.id === model) || !model ? models : [{ id: model, label: model }, ...models];
  const save = useMutation({ mutationFn: () => myMemoryApi.setKeeper(accountId, model || "default"), onSuccess: (d) => qc.setQueryData(["memory-keeper"], d) });
  if (k.isLoading) return <Spinner />;
  if (!accounts.length) return <Alert tone="warn">{t("myMemory.keeperNoAccount")}</Alert>;
  const dirty = k.data && (accountId !== (k.data.accountId ?? "") || model !== k.data.model);
  return (
    <Card className="space-y-3 p-4">
      <div>
        <h3 className="text-sm font-medium">{t("myMemory.keeperTitle")}</h3>
        <p className="mt-1 text-xs text-fg-muted">{t("myMemory.keeperHint")}</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t("myMemory.keeperProvider")}>
          <Select value={accountId} onChange={(e) => { setAccountId(e.target.value); const a = accounts.find((x) => x.id === e.target.value); setModel(a?.provider === "claude" ? "haiku" : "default"); }}>
            {accounts.map((a) => <option key={a.id} value={a.id}>{PROVIDERS[a.provider as AgentProvider]?.label ?? a.provider} · {a.label}</option>)}
          </Select>
        </Field>
        <Field label={t("chat.chooseModel")}>
          <Select value={model} onChange={(e) => setModel(e.target.value)}>
            {list.map((m) => <option key={m.id} value={m.id}>{m.id === "default" ? t("chat.models.default") : m.label}</option>)}
          </Select>
        </Field>
      </div>
      {!k.data?.chosen && !dirty && <p className="text-xs text-fg-muted">{t("myMemory.keeperDefault")}</p>}
      {save.error && <Alert tone="danger">{toMsg(save.error)}</Alert>}
      <Button size="sm" variant="secondary" loading={save.isPending} disabled={!dirty || !accountId} onClick={() => save.mutate()}>{t("common.save")}</Button>
    </Card>
  );
}

/** The user's own memory: the file every Chat section conversation of theirs reads (who they are, their rules). */
export function MyMemorySettings() {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  // the file is in the personal space: while it starts, ask again until it answers
  const q = useQuery({ queryKey: ["my-memory"], queryFn: myMemoryApi.get, refetchInterval: (s) => (s.state.data && !s.state.data.running ? 3000 : false) });
  const save = useMutation({ mutationFn: myMemoryApi.save, onSuccess: (d) => qc.setQueryData(["my-memory"], d) });
  if (q.isLoading) return <Spinner />;
  if (q.error) return <Alert tone="danger">{toMsg(q.error)}</Alert>;
  const m = q.data!;
  if (!m.running) return <p className="flex items-center gap-2 text-sm text-fg-muted"><Spinner /> {t("myMemory.starting")}</p>;
  return (
    <div className="space-y-3">
      <KeeperPicker />
      <Card className="flex h-96 flex-col overflow-hidden p-0">
        <MemoryEditor key={m.path} content={m.content} canEdit saving={save.isPending} onSave={(content) => save.mutate(content)} />
      </Card>
      {save.error && <Alert tone="danger">{toMsg(save.error)}</Alert>}
      {save.isSuccess && !save.isPending && <p className="text-xs text-success">{t("myMemory.saved")}</p>}
    </div>
  );
}
